import React, { useState } from 'react';

import './index.less';

import {
  AgentMessageEventToolCallDataInput,
  AgentMessageEventToolCallStatus,
  AgentMessageToolSchema,
} from '../../../../gamma-api';
import IbIcon from '../../common/IbIcon';
import IbTypography from '../../common/IbTypography';
import IbSpin from '../../common/IbSpin';
import IbButton from '../../common/IbButton';
import AgentMessageToolModal from '../../AgentMessageToolModal';

import { getToolCallEventIconName, getToolCallEventName } from './utils';

const MAIN_CLASS_NAME = 'tool-call-event';
const SELECTED_CLASS_NAME = `${MAIN_CLASS_NAME}_selected`;
const ERROR_CLASS_NAME = `${MAIN_CLASS_NAME}_error`;
const SUCCESS_CLASS_NAME = `${MAIN_CLASS_NAME}_success`;
const DEFAULT_SIZE_CLASS_NAME = `${MAIN_CLASS_NAME}_size-default`;
const SMALL_SIZE_CLASS_NAME = `${MAIN_CLASS_NAME}_size-small`;
const ICON_CONTAINER_CLASS_NAME = `${MAIN_CLASS_NAME}__icon-container`;
const SCHEMA_BUTTON_CLASS_NAME = `${MAIN_CLASS_NAME}__schema-button`;
const STATUS_CLASS_NAME = `${MAIN_CLASS_NAME}__status`;

export enum ToolCallEventSize {
  DEFAULT = 'default',
  SMALL = 'small',
}

const iconSizeMap = {
  [ToolCallEventSize.DEFAULT]: 20,
  [ToolCallEventSize.SMALL]: 16,
};

const paragraphTypeMap = {
  [ToolCallEventSize.DEFAULT]: 'normal' as const,
  [ToolCallEventSize.SMALL]: 'secondary' as const,
};

const spinSizeMap = {
  [ToolCallEventSize.DEFAULT]: 'default' as const,
  [ToolCallEventSize.SMALL]: 'small' as const,
};

export interface IToolCallEventProps {
  toolCallData: AgentMessageEventToolCallDataInput;
  toolSchema?: AgentMessageToolSchema;
  isSelected?: boolean;
  size?: ToolCallEventSize;
  onSelect?: () => void;
}

const ToolCallEvent: React.FC<IToolCallEventProps> = ({
  toolCallData,
  toolSchema,
  isSelected = false,
  size = ToolCallEventSize.DEFAULT,
  onSelect,
}) => {
  const [toolModalVisible, setToolModalVisible] = useState(false);

  const classes = [MAIN_CLASS_NAME];
  if (isSelected) {
    classes.push(SELECTED_CLASS_NAME);
  }
  if (toolCallData.status === AgentMessageEventToolCallStatus.Error) {
    classes.push(ERROR_CLASS_NAME);
  }
  if (toolCallData.status === AgentMessageEventToolCallStatus.Success) {
    classes.push(SUCCESS_CLASS_NAME);
  }
  if (size === ToolCallEventSize.DEFAULT) {
    classes.push(DEFAULT_SIZE_CLASS_NAME);
  }
  if (size === ToolCallEventSize.SMALL) {
    classes.push(SMALL_SIZE_CLASS_NAME);
  }

  const onClick = () => onSelect?.();

  const onSchemaClick = () => setToolModalVisible(true);

  return (
    <div className={classes.join(' ')} onClick={onClick}>
      <div className={ICON_CONTAINER_CLASS_NAME}>
        <IbIcon iconName={getToolCallEventIconName(toolCallData)} size={iconSizeMap[size]} />
      </div>
      <IbTypography.Paragraph type={paragraphTypeMap[size]}>
        {getToolCallEventName(toolCallData)}
      </IbTypography.Paragraph>
      {toolSchema ? (
        <div className={SCHEMA_BUTTON_CLASS_NAME}>
          <IbButton icon={<IbIcon iconName="code-brackets-two" />} type="icon" onClick={onSchemaClick} />
          <AgentMessageToolModal
            toolSchema={toolSchema}
            visible={toolModalVisible}
            onCancel={() => setToolModalVisible(false)}
          />
        </div>
      ) : (
        <>
          {toolCallData.status === AgentMessageEventToolCallStatus.Success && (
            <IbIcon className={STATUS_CLASS_NAME} iconName="check-one" size={iconSizeMap[size]} />
          )}
          {toolCallData.status === AgentMessageEventToolCallStatus.Error && (
            <IbIcon className={STATUS_CLASS_NAME} iconName="attention" size={iconSizeMap[size]} />
          )}
          {toolCallData.status === AgentMessageEventToolCallStatus.Processing && (
            <IbSpin className={STATUS_CLASS_NAME} size={spinSizeMap[size]} />
          )}
        </>
      )}
    </div>
  );
};

export default ToolCallEvent;
